import {
  incidentCustomerCommandSchema,
  incidentCustomerOptionsPageSchema,
  incidentCustomerOptionsQuerySchema,
  incidentDetailSchema,
  incidentIdempotencyKeySchema,
  incidentPathSchema,
} from '@solidops/shared/incidents';
import type {
  IncidentCustomerCommandDto,
  IncidentCustomerOptionsPageDto,
  IncidentCustomerOptionsQueryDto,
  IncidentDetailDto,
} from '@solidops/shared/incidents';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { phoneProgressSchema } from '@solidops/shared/phone';
import type { PhoneProgressDto } from '@solidops/shared/phone';
import {
  getIncidentPhone,
  getGetIncidentPhoneQueryOptions,
  getGetIncidentPhoneQueryKey,
} from './generated/phone/phone.js';
import {
  listIncidentCustomerOptions,
  getListIncidentCustomerOptionsQueryOptions,
  getListIncidentCustomerOptionsQueryKey,
  changeIncidentCustomer,
} from './generated/incidents/incidents.js';
import { getIncidentQueryKey, readIncident } from './incidents.js';
import type { IncidentVersionedCommandAuth, IncidentCommandOptions } from './incidents.commands.js';

export { getGetIncidentPhoneQueryKey as getIncidentPhoneQueryKey };
type IncidentProjectionQueryOptions<T> = Omit<UseQueryOptions<T, Error>, 'queryFn'> & {
  readonly queryKey: QueryKey;
  readonly queryFn: (context: { signal: AbortSignal }) => Promise<T>;
};

export function getIncidentPhoneQueryOptions(
  incidentId: string,
): IncidentProjectionQueryOptions<PhoneProgressDto> {
  return {
    ...getGetIncidentPhoneQueryOptions<PhoneProgressDto, Error>(incidentId),
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<PhoneProgressDto> =>
      readIncident(
        () => getIncidentPhone(incidentPathSchema.parse({ incidentId }).incidentId, { signal }),
        phoneProgressSchema,
        getGetIncidentPhoneQueryKey(incidentId),
      ),
  };
}

export function getIncidentCustomerOptionsQueryOptions(
  incidentId: string,
  input: IncidentCustomerOptionsQueryDto,
): IncidentProjectionQueryOptions<IncidentCustomerOptionsPageDto> {
  const params = incidentCustomerOptionsQuerySchema.parse(input);
  return {
    ...getListIncidentCustomerOptionsQueryOptions<IncidentCustomerOptionsPageDto, Error>(
      incidentId,
      params,
    ),
    queryFn: async ({ signal }: { signal: AbortSignal }) =>
      readIncident(
        () =>
          listIncidentCustomerOptions(incidentPathSchema.parse({ incidentId }).incidentId, params, {
            signal,
          }),
        incidentCustomerOptionsPageSchema,
        getListIncidentCustomerOptionsQueryKey(incidentId, params),
      ),
  };
}

export async function requestIncidentCustomerChange(
  incidentId: string,
  input: IncidentCustomerCommandDto,
  auth: IncidentVersionedCommandAuth,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const id = incidentPathSchema.parse({ incidentId }).incidentId;
  return readIncident(
    () =>
      changeIncidentCustomer(id, incidentCustomerCommandSchema.parse(input), {
        headers: {
          'x-csrf-token': auth.csrfToken,
          'Idempotency-Key': incidentIdempotencyKeySchema.parse(auth.idempotencyKey),
        },
        ...(options?.signal ? { signal: options.signal } : {}),
      }),
    incidentDetailSchema,
    getIncidentQueryKey(id),
  );
}
